import {
  Button,
  Flex,
  Heading,
  Square,
  Stack,
  SvgX,
  Copy,
  SvgCaretLeft,
  Radio,
} from '@grubhubprod/gh-cookbook-react/dist';
import React from 'react';
import { Address } from '@grubhubprod/order-taking-client-sdk/src/sdk/types/geocode';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { Modal } from 'src/components/Modal/Modal';

export interface IAddressConfirmationModal {
  onClose: () => void;
  onSuccess: (address: Address) => void;
  suggestedGeoAddresses: Address[];
  currentAddressString: string;
  isAccount?: boolean;
}

export function AddressConfirmationModal({
  onClose,
  onSuccess,
  suggestedGeoAddresses,
  currentAddressString,
  isAccount,
}: IAddressConfirmationModal): JSX.Element {
  const { register, watch, setValue, handleSubmit } = useForm({
    mode: 'onChange',
    defaultValues: {
      address: getUniqueAddressIdentifier(suggestedGeoAddresses[0]),
    },
  });

  const addressValue = watch('address');

  function getAddress({
    address_region,
    address_locality,
    address_sublocality,
    street_address1,
    postal_code,
  }: Address) {
    return `${street_address1}, ${
      address_locality || address_sublocality
    }, ${address_region}, ${postal_code || ''}`;
  }

  function getUniqueAddressIdentifier(address: Address) {
    return `${address.latitude}|${address.longitude}`;
  }

  function onFormSubmit({ address: value }) {
    const [latitude, longitude] = value.split('|');
    const address = suggestedGeoAddresses.find(
      (a) => a.latitude === latitude && a.longitude === longitude
    );
    onSuccess(address);
  }

  return (
    <Modal size="sm">
      <Square as="header" dimension="2" width="100%">
        <Button
          variant="tertiary"
          type="button"
          onClick={onClose}
          className="closeModalButton"
        >
          {isAccount ? <SvgCaretLeft size="24px" /> : <SvgX size="24px" />}
        </Button>
        <Square inset="md">
          <Heading variant="h1">Confirm your address</Heading>
        </Square>
      </Square>
      <Square inset="md">
        <Stack y="sm">
          We weren’t able to verify your address. Pick one of the recommended
          options.
        </Stack>
        <Stack y="sm" borderBottom="1px solid" borderColor="lightLine">
          <Stack y="sm">
            <Heading variant="h5">Your address</Heading>
            <Flex justifyContent="start" alignItems="top">
              <Stack x="md">
                <span>{currentAddressString}</span>
              </Stack>
              <button
                onClick={onClose}
                className="editButtonAddressConfirmation"
              >
                <Copy variant="body" color="interactive">
                  Edit
                </Copy>
              </button>
            </Flex>
          </Stack>
        </Stack>
        <Stack y="sm">
          <Heading variant="h5">Recommended options</Heading>
          {suggestedGeoAddresses.map((address, index) => (
            <Radio
              key={getUniqueAddressIdentifier(address)}
              id={`id-${index}`}
              name="address"
              value={getUniqueAddressIdentifier(address)}
              ref={register}
              checked={addressValue === getUniqueAddressIdentifier(address)}
              onChange={() =>
                setValue('address', getUniqueAddressIdentifier(address))
              }
            >
              {getAddress(address)}
            </Radio>
          ))}
        </Stack>
      </Square>
      <Square as="footer" dimension="2" inset="md">
        <Flex>
          <Stack x="x-sm">
            <Button
              variant="primary"
              buttonSize="medium"
              onClick={handleSubmit(onFormSubmit)}
            >
              Update address
            </Button>
          </Stack>
          <Button variant="secondary" buttonSize="medium" onClick={onClose}>
            Cancel
          </Button>
        </Flex>
      </Square>
    </Modal>
  );
}
